import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { 
  PlusIcon, 
  ExclamationCircleIcon, 
  BookOpenIcon,
  EyeIcon,
  EyeOffIcon,
  TrashIcon,
  ArrowLeftIcon
} from '@heroicons/react/outline';
import { toastService } from '../../../../../services/toast';
import { coursesApi } from '../../../../../services/api/courses.api';
import Tooltip from '../../../../common/Tooltip';
import QuizModal from './components/QuizModal';
import ResourceModal from './components/ResourceModal';
import GroupModal from './components/GroupModal';
import AlertConfirmModal from '../../../../common/AlertConfirmModal';
import DeleteConfirmModal from '../../../../common/DeleteConfirmModal';
import PublishConfirmModal from './components/modals/PublishConfirmModal';

// المكونات
import Section from './components/Section';
import AddSectionModal from './components/modals/AddSectionModal';
import AddLessonModal from './components/modals/AddLessonModal';
import AddQuizModal from './components/modals/AddQuizModal';
import EditSectionModal from './components/modals/EditSectionModal';
import EditLessonModal from './components/modals/EditLessonModal';

const CourseContentManage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [sections, setSections] = useState([]);
  
  // حالات النوافذ المنبثقة
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [showEditSectionModal, setShowEditSectionModal] = useState(false);
  const [showAddLessonModal, setShowAddLessonModal] = useState(false);
  const [showAddQuizModal, setShowAddQuizModal] = useState(false);
  const [showDeleteSectionModal, setShowDeleteSectionModal] = useState(false);
  const [showDeleteLessonModal, setShowDeleteLessonModal] = useState(false);
  const [showEditLessonModal, setShowEditLessonModal] = useState(false);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  
  // العناصر المحددة
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);
  const [resourceToEdit, setResourceToEdit] = useState(null);
  const [showResourceModal, setShowResourceModal] = useState(false);
  
  // إعداد أجهزة الاستشعار للسحب والإفلات
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // جلب محتوى الدورة
  useEffect(() => {
    fetchCourseContent();
  }, [courseId]);
  
  // دالة لجلب اختبارات قسم معين
  const fetchSectionExams = async (sectionId) => {
    try {
      // استخدام coursesApi للحصول على اختبارات القسم
      const response = await coursesApi.getSectionExams(sectionId);
      return Array.isArray(response) ? response : (response.data || []);
    } catch (error) {
      // خطأ في جلب اختبارات القسم
      return [];
    }
  };
  
  // دالة لجلب محتوى القسم مرتباً (دروس، حقائب، اختبارات)
  const fetchOrderedContent = async (sectionId) => {
    try {
      setLoading(true);
      // جلب محتوى القسم مرتباً
      
      const response = await coursesApi.getOrderedContent(sectionId);
      
      const orderedItems = response.items || [];
      
      // تحديث state الخاص بالأقسام بالبيانات الجديدة
      setSections(prevSections => {
        return prevSections.map(section => {
          if (section.id === parseInt(sectionId)) {
            // تحديث القسم بالعناصر الجديدة
            return {
              ...section,
              items: orderedItems
            };
          }
          return section;
        });
      });
      
      // إذا كان هناك قسم مُرجَع، نحتفظ به
      if (response.section) {
        const updatedSection = {
          ...response.section,
          items: orderedItems
        };
        setSelectedSection(updatedSection);
      }
      
      return orderedItems;
    } catch (error) {
      // خطأ في جلب محتوى القسم المرتب
      toastService.error('حدث خطأ أثناء جلب محتوى القسم');
      return [];
    } finally {
      setLoading(false);
    }
  };
  
  // دالة لتحديث ترتيب المحتوى
  const updateContentOrder = async (sectionId, items) => {
    try {
      // تحديث ترتيب محتوى القسم
      
      // إعادة تعيين الترتيب بشكل واضح لكل عنصر لضمان ترتيب تسلسلي
      const updatedItems = items.map((item, index) => {
        return {
          ...item,
          order: index  // إعادة تعيين الترتيب باستخدام مؤشر العنصر في المصفوفة
        };
      });
      
      // العناصر بقيم الترتيب الجديدة
      
      // تحضير بيانات الترتيب للإرسال إلى API
      const orderData = updatedItems.map((item, index) => ({
        id: item.id,
        type: item.type, // 'lesson', 'quiz', 'resource'
        order: index
      }));
      
      // بيانات الترتيب للإرسال إلى API
      
      // إرسال طلب تحديث الترتيب
      await coursesApi.updateContentOrder(sectionId, { items: orderData });
      
      // تحديث الواجهة
      setSections(prevSections => {
        return prevSections.map(section => {
          if (section.id === sectionId) {
            return {
              ...section,
              items: items
            };
          }
          return section;
        });
      });
      
      toastService.success('تم تحديث ترتيب المحتوى بنجاح');
    } catch (error) {
      // خطأ في تحديث ترتيب المحتوى
      toastService.error('حدث خطأ أثناء تحديث ترتيب المحتوى');
      
      // في حالة الخطأ، نحاول إعادة جلب المحتوى مرتبًا من الخادم
      try {
        // محاولة إعادة جلب المحتوى المرتب
        await fetchOrderedContent(sectionId);
      } catch (refetchError) {
        // فشل في إعادة جلب المحتوى بعد خطأ تحديث الترتيب
      }
    }
  };
  
  const fetchCourseContent = async () => {
    setLoading(true);
    try {
      // جلب تفاصيل الدورة
      const courseResponse = await coursesApi.getById(courseId);
      const courseData = courseResponse.data;
      
      // جلب محتوى الدورة (الأقسام)
      const contentResponse = await coursesApi.getCourseContent(courseId);
      
      // استجابة محتوى الدورة
      
      setCourse(courseData);
      
      // تنسيق الأقسام بدون المحتوى
      let formattedSections = contentResponse.map(section => ({
        ...section,
        isExpanded: false, // افتراضيًا مغلق
        items: [] // سنملأ المحتوى لاحقًا من نظام الترتيب الجديد
      }));
      
      // جلب محتوى كل قسم باستخدام النظام الجديد الموحد
      // جلب المحتوى المرتب لجميع الأقسام
      const sectionsWithContentPromises = formattedSections.map(async (section) => {
        try {
          // جلب المحتوى المرتب للقسم
          const orderedItems = await fetchOrderedContent(section.id);
          // تم جلب العناصر المرتبة للقسم
          
          // معالجة العناصر لتنسيقها بالشكل المناسب للواجهة
          const processedItems = orderedItems.map(item => {
            // معالجة الدروس
            if (item.type === 'lesson') {
              // تحويل نوع الدرس إلى الصيغة المستخدمة في الفرونتإند
              let lessonType = item.lesson_type;
              
              if (lessonType === 'video') {
                if (item.content_url && item.content_url.startsWith('/storage/course_videos/')) {
                  lessonType = 'video_file';
                } else {
                  lessonType = 'video_url';
                }
              }
              
              return {
                ...item,
                lesson_type: lessonType
              };
            }
            
            // معالجة الاختبارات
            if (item.type === 'quiz') {
              return {
                ...item,
                questionsCount: item.questions_count || 0
              };
            }
            
            // معالجة الحقائب
            if (item.type === 'resource') {
              return {
                ...item,
                resourceType: item.resource_type
              };
            }
            
            return item;
          });
          
          return {
            ...section,
            items: processedItems
          };
        } catch (error) {
          // خطأ في جلب المحتوى المرتب للقسم
          return section;
        }
      });
      
      try {
        // انتظار جلب محتوى جميع الأقسام
        formattedSections = await Promise.all(sectionsWithContentPromises);
        // تم جلب محتوى جميع الأقسام بنجاح
      } catch (error) {
        // خطأ في جلب محتوى القسم
      }
      
      // التأكد من وجود حقل order لكل قسم ودرس
      const sectionsWithOrder = formattedSections.map(section => {
        // إذا لم يكن هناك حقل order، نضع قيمة افتراضية
        const sectionWithOrder = {
          ...section,
          order: section.order || 999 // قيمة كبيرة لوضعها في النهاية
        };
        
        // التأكد من ترتيب الدروس داخل القسم
        if (sectionWithOrder.items && sectionWithOrder.items.length > 0) {
          const itemsWithOrder = sectionWithOrder.items.map(item => ({
            ...item,
            order: item.order || 999 // قيمة كبيرة لوضعها في النهاية
          }));
          
          // لا نقوم بأي ترتيب للدروس، نتركها كما هي بترتيب الإضافة
          // لا نقوم بترتيب الدروس، نحافظ على الترتيب الأصلي
          
          // استخدام الترتيب الأصلي للدروس كما تم إضافتها
          const sortedItems = itemsWithOrder;
          
          return { ...sectionWithOrder, items: sortedItems };
        }
        
        return sectionWithOrder;
      });
      
      // ترتيب الأقسام حسب حقل order
      const fullySortedSections = [...sectionsWithOrder].sort((a, b) => {
        // ترتيب الأقسام
        return a.order - b.order;
      });
      
      // بيانات الأقسام وترتيبها
      
      setSections(fullySortedSections);
      setLoading(false);
      // تم تحميل محتوى الدورة مع الاختبارات
    } catch (error) {
      // خطأ في جلب محتوى الدورة
      toastService.error('حدث خطأ أثناء جلب محتوى الدورة');
    } finally {
      setLoading(false);
    }
  };
  
  // إضافة قسم جديد
  const handleAddSection = async (sectionData) => {
    try {
      const newSection = await coursesApi.addSection(courseId, {
        ...sectionData,
        order: sections.length // وضع القسم الجديد في النهاية
      });
      
      // إضافة القسم الجديد للقائمة
      setSections(prevSections => [
        ...prevSections,
        {
          ...newSection,
          isExpanded: false,
          items: []
        }
      ]);
      
      setShowAddSectionModal(false);
      toastService.success('تم إضافة القسم بنجاح');
    } catch (error) {
      // خطأ في إضافة القسم
      toastService.error('حدث خطأ أثناء إضافة القسم');
    }
  };
  
  // تعديل قسم
  const handleEditSection = async (sectionData) => {
    if (!selectedSection) return;
    
    try {
      const updatedSection = await coursesApi.updateSection(selectedSection.id, sectionData);
      
      // تحديث القسم في القائمة
      setSections(prevSections => 
        prevSections.map(section => 
          section.id === selectedSection.id
            ? {
                ...section,
                ...updatedSection
              }
            : section
        )
      );
      
      setShowEditSectionModal(false);
      setSelectedSection(null);
      toastService.success('تم تعديل القسم بنجاح');
    } catch (error) {
      // خطأ في تحديث القسم
      toastService.error('حدث خطأ أثناء تعديل القسم');
    }
  };
  
  // حذف قسم
  const handleDeleteSection = async () => {
    if (!selectedSection) return;
    
    try {
      await coursesApi.deleteSection(selectedSection.id);
      
      // حذف القسم من القائمة
      setSections(prevSections => 
        prevSections.filter(section => section.id !== selectedSection.id)
      );
      
      setShowDeleteSectionModal(false);
      setSelectedSection(null);
      toastService.success('تم حذف القسم بنجاح');
    } catch (error) {
      // خطأ في حذف القسم
      
      // التحقق من نوع الخطأ
      if (error.response && error.response.status === 422) {
        // خطأ تحقق - القسم يحتوي على دروس
        toastService.error(error.response.data.message || 'لا يمكن حذف القسم لأنه يحتوي على محتوى');
      } else {
        // خطأ عام
        toastService.error('حدث خطأ أثناء حذف القسم');
      }
    }
  };
  
  // إضافة درس جديد
  const handleAddLesson = async (lessonData, onProgress) => {
    if (!selectedSection) return;
    
    try {
      // إضافة درس إلى القسم
      
      // تحديد الترتيب بناءً على عدد الدروس الحالية
      // نضيف 1 للتأكد من أن الدرس الجديد سيكون في النهاية
      let order = 1; // القيمة الافتراضية إذا لم يكن هناك دروس
      
      if (selectedSection.items && selectedSection.items.length > 0) {
        // نحسب أعلى ترتيب موجود ونضيف له 1
        const maxOrder = Math.max(...selectedSection.items.map(item => parseInt(item.order) || 0));
        order = maxOrder + 1;
      }
      
      // إضافة درس جديد بترتيب محدد للقسم
      
      // تأكد من أن المدة هي رقم صحيح
      let durationInMinutes = parseInt(lessonData.duration) || 0;
      
      const newLesson = await coursesApi.addLesson(
        selectedSection.id,
        {
          ...lessonData,
          duration: durationInMinutes.toString(),
          order: order
        },
        onProgress
      );
      
      // تمت إضافة الدرس بنجاح
      
      // الحصول على بيانات الدرس المضاف من استجابة API
      const lessonToAdd = newLesson.lesson || newLesson;
      
      // تنسيق البيانات لتتناسب مع واجهة المستخدم
      let lessonType = lessonToAdd.lesson_type || lessonData.type;
      if (lessonType === 'video') {
        // تحديد نوع الفيديو (ملف أو رابط) بناء على المسار
        if (lessonToAdd.content_url && lessonToAdd.content_url.startsWith('/storage/course_videos/')) {
          lessonType = 'video_file';
        } else {
          lessonType = 'video_url';
        }
      }
      
      // تحضير الدرس المنسق للإضافة للواجهة
      const formattedLesson = {
        ...lessonToAdd,
        type: 'lesson',
        lesson_type: lessonType,
        order: lessonToAdd.order || order
      };
      
      // إضافة الدرس مباشرة لحالة التطبيق
      setSections(prevSections => {
        return prevSections.map(section => {
          if (section.id !== selectedSection.id) return section;
          
          const currentItems = section.items || [];
          return {
            ...section,
            items: [...currentItems, formattedLesson]
          };
        });
      });
      
      // تحديث القسم المحدد أيضًا لضمان اتساق البيانات
      setSelectedSection(prev => {
        if (!prev) return prev;
        const items = prev.items || [];
        return {
          ...prev,
          items: [...items, formattedLesson]
        };
      });
      
      setShowAddLessonModal(false);
      toastService.success('تم إضافة الدرس بنجاح');
    } catch (error) {
      // خطأ في إضافة الدرس
      toastService.error('حدث خطأ أثناء إضافة الدرس');
    }
  };
  
  // حذف درس
  const handleDeleteLesson = async (lesson) => {
    if (!lesson) return;
    
    try {
      // حذف الدرس
      
      // حذف الدرس
      await coursesApi.deleteLesson(lesson.id);
      
      // تحديث واجهة المستخدم بعد الحذف
      setSections(prevSections => 
        prevSections.map(section => ({
          ...section,
          items: section.items.filter(item => item.id !== lesson.id)
        }))
      );
      
      setShowDeleteLessonModal(false);
      setSelectedLesson(null);
      toastService.success('تم حذف الدرس بنجاح');
    } catch (error) {
      // خطأ في حذف الدرس
      toastService.error('حدث خطأ أثناء حذف الدرس');
    }
  };
  
  // تبديل حالة توسيع القسم
  const handleToggleSection = (sectionId) => {
    setSections(prevSections => 
      prevSections.map(section => ({
        ...section,
        // إذا كان هذا هو القسم المحدد، نقوم بعكس حالته
        // وإلا نقوم بإغلاق جميع الأقسام الأخرى
        isExpanded: section.id === sectionId ? !section.isExpanded : false
      }))
    );
  };
  
  // معالجة السحب والإفلات للأقسام
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    // نسخة من الأقسام الحالية
    let currentSections = [...sections];
    
    // تحديد المؤشرات
    const oldIndex = currentSections.findIndex(section => section.id === active.id);
    const newIndex = currentSections.findIndex(section => section.id === over.id);
    
    // تغيير الترتيب محليًا
    currentSections = arrayMove(currentSections, oldIndex, newIndex);
    
    // تحديث الترتيب في الواجهة فورًا
    setSections(currentSections);
    
    // تحديث ترتيب الأقسام في قاعدة البيانات
    try {
      // تحديث ترتيب الأقسام للدورة
      
      // إعداد الترتيب الجديد
      const sectionsOrder = currentSections.map((section, index) => ({
        id: section.id,
        order: index + 1
      }));
      
      // ترتيب الأقسام الجديد
      
      // إرسال الترتيب للباك إند
      const response = await coursesApi.updateSectionsOrder(courseId, sectionsOrder);
      // استجابة الخادم
      
      // منع إعادة تحميل البيانات
      // لا نقوم بإعادة تحميل البيانات من السيرفر لمنع إعادة ضبط الترتيب
      
      // تحديث الأقسام مرة أخرى للتأكد من الترتيب
      setSections(prevSections => {
        // نسخة جديدة من الأقسام
        const newSections = [...prevSections];
        
        // تحديث الترتيب لكل قسم
        sectionsOrder.forEach(item => {
          const sectionIndex = newSections.findIndex(s => s.id === item.id);
          if (sectionIndex !== -1) {
            newSections[sectionIndex].order = item.order;
          }
        });
        
        // ترتيب الأقسام حسب الترتيب الجديد
        return [...newSections].sort((a, b) => a.order - b.order);
      });
      
      // إظهار رسالة نجاح
      toastService.success('تم تحديث ترتيب الأقسام بنجاح');
    } catch (error) {
      // خطأ في تحديث ترتيب الأقسام
      toastService.error('حدث خطأ أثناء تحديث ترتيب الأقسام');
      // إعادة جلب المحتوى في حالة الخطأ
      fetchCourseContent();
    }
  };
  
  // معالجة السحب والإفلات لجميع عناصر المحتوى (دروس، حقائب، اختبارات)
  const handleLessonsDragEnd = async (event, sectionId) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    // الحصول على القسم الحالي
    const currentSection = sections.find(s => s.id === sectionId);
    if (!currentSection) return;
    
    // نسخة من العناصر الحالية
    const currentItems = [...currentSection.items];
    
    // تحديد المؤشرات
    const oldIndex = currentItems.findIndex(item => item.id === active.id);
    const newIndex = currentItems.findIndex(item => item.id === over.id);
    
    // تغيير الترتيب محليًا
    const reorderedItems = arrayMove(currentItems, oldIndex, newIndex);
    
    // تحديث الترتيب في الواجهة فورًا
    setSections(prevSections => 
      prevSections.map(section => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          items: reorderedItems
        };
      })
    );
    
    // تحديث ترتيب المحتوى في قاعدة البيانات باستخدام النظام الجديد
    try {
      // تحديث ترتيب المحتوى للقسم
      
      // استخدام الدالة الجديدة لتحديث ترتيب جميع أنواع المحتوى معًا
      await updateContentOrder(sectionId, reorderedItems);
      
      // تم تحديث الواجهة بالفعل في الخطوة السابقة
      // وتم إظهار رسالة النجاح في دالة updateContentOrder
    } catch (error) {
      // خطأ في تحديث ترتيب المحتوى
      toastService.error('حدث خطأ أثناء تحديث ترتيب المحتوى');
      // إعادة جلب المحتوى في حالة الخطأ
      fetchCourseContent();
    }
  };
  
  // تغيير حالة الدرس (نشر/إخفاء)
  const handleLessonStatusChange = async (lessonId) => {
    try {
      // العثور على الدرس والقسم الذي يحتويه
      let targetSection = null;
      let targetLesson = null;
      
      sections.forEach(section => {
        const lesson = section.items?.find(item => item.id === lessonId);
        if (lesson) {
          targetSection = section;
          targetLesson = lesson;
        }
      });
      
      if (!targetLesson || !targetSection) {
        // الدرس أو القسم غير موجود
        return;
      }

      // تغيير حالة الدرس في الباك إند
      const response = await coursesApi.toggleLessonStatus(lessonId);
      
      // تحديث الواجهة بالقيمة المستلمة من الباك إند
      setSections(prevSections => 
        prevSections.map(section => {
          if (section.id !== targetSection.id) return section;
          
          return {
            ...section,
            items: section.items.map(item => 
              item.id === lessonId ? { ...item, is_published: response.lesson.is_published } : item
            )
          };
        })
      );
      
      // عرض الرسالة المستلمة من الباك إند
      toastService.success(response.message);

    } catch (error) {
      // خطأ في تغيير حالة الدرس
      toastService.error('حدث خطأ أثناء تغيير حالة الدرس');
      fetchCourseContent();
    }
  };
  
  // تغيير حالة الاختبار (نشر/إخفاء)
  const handleQuizStatusChange = async (quizId) => {
    try {
      // تغيير حالة الاختبار
      const response = await coursesApi.toggleExamStatus(quizId);
      
      // تحديث حالة الاختبار في قائمة الأقسام
      setSections(prevSections => {
        return prevSections.map(section => {
          if (!section.items) return section;
          
          return {
            ...section,
            items: section.items.map(item => {
              if (item.type === 'quiz' && item.id === quizId) {
                return {
                  ...item,
                  status: item.status === 'published' ? 'draft' : 'published'
                };
              }
              return item;
            })
          };
        });
      });
      
      toastService.success(response.message || 'تم تغيير حالة الاختبار بنجاح');
    } catch (error) {
      // خطأ في تغيير حالة الاختبار
      toastService.error('حدث خطأ أثناء تغيير حالة الاختبار');
    }
  };
  
  // تغيير حالة المورد (نشر/إخفاء)
  const handleResourceStatusChange = async (resourceId) => {
    try {
      // تغيير حالة المورد
      const response = await coursesApi.toggleResourceStatus(resourceId);
      
      // تحديث حالة المورد في قائمة الأقسام
      setSections(prevSections => {
        return prevSections.map(section => {
          if (!section.items) return section;
          
          return {
            ...section,
            items: section.items.map(item => {
              if (item.type === 'resource' && item.id === resourceId) {
                return {
                  ...item,
                  status: item.status === 'published' ? 'draft' : 'published'
                };
              }
              return item;
            })
          };
        });
      });
      
      toastService.success(response.message || 'تم تغيير حالة المورد بنجاح');
    } catch (error) {
      // خطأ في تغيير حالة المورد
      toastService.error('حدث خطأ أثناء تغيير حالة المورد');
    }
  };
  
  // تغيير حالة القسم (نشر/إخفاء)
  const handleSectionStatusChange = async (sectionId) => {
    try {
      // العثور على القسم
      const targetSection = sections.find(section => section.id === sectionId);
      
      if (!targetSection) {
        // القسم غير موجود
        return;
      }
      
      // تغيير حالة القسم
      const updatedSection = await coursesApi.toggleSectionStatus(sectionId);
      
      // تحديث حالة القسم في واجهة المستخدم
      setSections(prevSections => 
        prevSections.map(section => 
          section.id === sectionId ? { ...section, status: updatedSection.status } : section
        )
      );
      
      toastService.success(`تم ${updatedSection.status === 'published' ? 'نشر' : 'إخفاء'} القسم بنجاح`);
    } catch (error) {
      // خطأ في تغيير حالة القسم
      toastService.error('حدث خطأ أثناء تغيير حالة القسم');
    }
  };
  
  // تعديل الدرس (يدعم تمرير كائن الدرس لتجنّب تعارض المعرفات مع الاختبارات)
  const handleEditLesson = (lessonOrId) => {
    // لو تم تمرير كائن الدرس مباشرة، نستخدمه
    if (lessonOrId && typeof lessonOrId === 'object') {
      const obj = lessonOrId;
      // التأكد أننا نتعامل مع درس وليس اختبار
      if (obj.type && obj.type !== 'lesson' && obj.type !== 'video' && obj.type !== 'video_url' && obj.type !== 'video_file' && obj.type !== 'text' && obj.type !== 'file' && obj.type !== 'video_embed') {
        return; // ليس درساً
      }
      // العثور على القسم الذي يحتوي هذا الدرس بالهوية نفسها ونفس النوع إن أمكن
      const section = sections.find(sec => (sec.items || []).some(it => it.id === obj.id && (it.type === 'lesson' || it.type === obj.type)));
      if (!section) return;
      setSelectedSection(section);
      setSelectedLesson(obj);
      setShowEditLessonModal(true);
      return;
    }

    // وإلا تم تمرير معرف: نبحث عن الدرس مع تفضيل النوع lesson لتجنّب تضارب المعرفات
    const lessonId = lessonOrId;
    let foundSection = null;
    let foundLesson = null;
    for (const section of sections) {
      const candidate = (section.items || []).find(it => it.id === lessonId && (it.type === 'lesson' || it.type === 'video' || it.type === 'video_url' || it.type === 'video_file' || it.type === 'text' || it.type === 'file' || it.type === 'video_embed'));
      if (candidate) {
        foundSection = section;
        foundLesson = candidate;
        break;
      }
    }
    if (!foundLesson) return;
    setSelectedSection(foundSection);
    setSelectedLesson(foundLesson);
    setShowEditLessonModal(true);
  };
  
  // تعديل الدرس
  const handleUpdateLesson = async (lessonData) => {
    if (!selectedLesson || !selectedSection) return;
    
    try {
      // تحديث الدرس
      
      const updatedLesson = await coursesApi.updateLesson(selectedLesson.id, lessonData);
      
      // تم تحديث الدرس بنجاح
      
      // تحديث الدرس في قائمة الأقسام
      setSections(prevSections => {
        const newSections = prevSections.map(section => {
          if (section.id !== selectedSection.id) return section;
          
          return {
            ...section,
            items: section.items.map(item => {
              if (item.id === selectedLesson.id) {
                // تأكد من تحديث جميع خصائص الدرس
                // تأكد من تحديث جميع خصائص الدرس
                const updatedItem = {
                  ...item,
                  ...updatedLesson,
                  is_free: lessonData.is_free, // تأكد من تحديث حالة المجاني
                  lesson_type: lessonData.type // تأكد من تحديث نوع الدرس
                };
                
                // عنصر الدرس المحدث
                return updatedItem;
              }
              return item;
            })
          };
        });
        
        // الأقسام المحدثة بعد تحديث الدرس
        return newSections;
      });
      
      setShowEditLessonModal(false);
      setSelectedLesson(null);
      setSelectedSection(null);
      toastService.success('تم تعديل الدرس بنجاح');
    } catch (error) {
      // خطأ في تحديث الدرس
      toastService.error('حدث خطأ أثناء تعديل الدرس');
    }
  };
  
  // إضافة قروب جديد
  const handleAddGroup = async (groupData) => {
    if (!selectedSection) {
      // لم يتم تحديد قسم لإضافة المجموعة
      return;
    }
    
    try {
      // إضافة مجموعة جديدة إلى القسم
      
      // تحويل البيانات لتتوافق مع الباك إند
      const dataToSend = {
        title: groupData.title,
        description: groupData.description,
        type: groupData.groupType, // الباك إند يتوقع type بدلاً من groupType
        link: groupData.link
      };
      
      // البيانات المرسلة إلى الخلفية
      
      // إرسال بيانات القروب للباك إند
      const response = await coursesApi.addGroup(selectedSection.id, dataToSend);
      // استجابة من API إضافة المجموعة
      
      if (response && response.group) {
        // تحويل البيانات من الباك إند إلى التنسيق المطلوب في الفرونت إند
        const newGroup = {
          id: response.group.id,
          type: 'group',
          title: response.group.title,
          description: response.group.description || '',
          groupType: response.group.type, // تحويل type من الباك إند إلى groupType في الفرونت إند
          link: response.group.link || '',
          order: response.group.order || 0,
          status: response.group.status || 'draft'
        };
        
        // تم إنشاء كائن المجموعة الجديدة
        
        // تحديث واجهة المستخدم بإضافة القروب الجديد - باستخدام map لخلق نسخ جديدة كاملة
        setSections(prevSections => {
          const newSections = prevSections.map(section => {
            // إذا لم يكن هذا هو القسم المطلوب، نعيده كما هو
            if (section.id !== selectedSection.id) return section;
            
            // تأكد من وجود مصفوفة items
            const currentItems = section.items || [];
            
            // إنشاء نسخة جديدة من القسم مع إضافة القروب الجديد
            return {
              ...section,
              items: [...currentItems, newGroup]
            };
          });
          
          // الأقسام المحدثة بعد إضافة المجموعة
          return newSections;
        });
        
        // تحديث القسم المحدد أيضًا
        setSelectedSection(prev => {
          if (!prev) return prev;
          const currentItems = prev.items || [];
          return {
            ...prev,
            items: [...currentItems, newGroup]
          };
        });
        
        toastService.success('تم إضافة القروب بنجاح');
        
        // إغلاق مودال إضافة القروب
        setShowAddGroupModal(false);
      }
    } catch (error) {
      // خطأ في إضافة المجموعة
      toastService.error('حدث خطأ أثناء إضافة القروب');
    }
  };

  // تعديل قروب
  const handleEditGroup = async (groupId) => {
    try {
      // تحرير المجموعة
      
      // البحث عن القروب في الأقسام
      let foundGroup = null;
      let foundSection = null;
      
      for (const section of sections) {
        const group = section.items?.find(item => 
          item.id === groupId && item.type === 'group'
        );
        
        if (group) {
          foundGroup = group;
          foundSection = section;
          break;
        }
      }
      
      if (foundGroup) {
        // تم العثور على المجموعة للتحرير
        
        // تعيين القروب والقسم المحددين
        setSelectedGroup(foundGroup);
        setSelectedSection(foundSection);
        
        // فتح المودال
        setShowEditGroupModal(true);
      } else {
        // لم يتم العثور على المجموعة
        toastService.error('لم يتم العثور على القروب');
      }
    } catch (error) {
      // خطأ في معالجة تحرير المجموعة
      toastService.error('حدث خطأ أثناء تحميل بيانات القروب');
    }
  };

  // تحديث قروب
  const handleUpdateGroup = async (groupData) => {
    if (!selectedGroup) {
      // لم يتم تحديد مجموعة للتحديث
      return;
    }
    
    try {
      // تحديث المجموعة
      
      // تحويل البيانات لتتوافق مع الباك إند
      const dataToSend = {
        title: groupData.title,
        description: groupData.description,
        type: groupData.groupType, // الباك إند يتوقع type بدلاً من groupType
        link: groupData.link
      };
      
      // إرسال بيانات القروب للباك إند
      const updatedGroup = await coursesApi.updateGroup(selectedGroup.id, dataToSend);
      // استجابة تحديث المجموعة
      
      if (updatedGroup && updatedGroup.group) {
        const formattedGroup = {
          id: updatedGroup.group.id,
          type: 'group',
          title: updatedGroup.group.title,
          description: updatedGroup.group.description || '',
          groupType: updatedGroup.group.type, // تحويل type من الباك إند إلى groupType في الفرونت إند
          link: updatedGroup.group.link || '',
          order: updatedGroup.group.order || 0,
          status: updatedGroup.group.status || 'draft'
        };
        
        // المجموعة المنسقة بعد التحديث
        
        // تحديث القروب في قائمة الأقسام - بالطريقة نفسها كما في تحديث الدرس
        setSections(prevSections => {
          const newSections = prevSections.map(section => {
            // إذا لم يكن القسم يحتوي على القروب المعني، نعيده كما هو
            return {
              ...section,
              items: section.items.map(item => {
                if (item.id === selectedGroup.id && item.type === 'group') {
                  // تحديث القروب بالبيانات الجديدة
                  return {
                    ...item,
                    ...formattedGroup
                  };
                }
                return item;
              })
            };
          });
          
          // الأقسام المحدثة بعد تحديث المجموعة
          return newSections;
        });
        
        // إغلاق المودال وتفريغ المجموعة المحددة
        setShowEditGroupModal(false);
        setSelectedGroup(null);
        toastService.success('تم تحديث القروب بنجاح');
      }
    } catch (error) {
      // خطأ في تحديث المجموعة
      toastService.error('حدث خطأ أثناء تحديث القروب');
    }
  };

  // تعديل القروب - تم دمجها مع الدالة السابقة
  
  // تغيير حالة القروب (نشر/إخفاء)
  const handleGroupStatusChange = async (groupId) => {
    try {
      // تغيير حالة المجموعة
      
      // إرسال طلب تغيير الحالة للباك إند
      const response = await coursesApi.toggleGroupStatus(groupId);
      
      if (response && response.group) {
        // تحديث واجهة المستخدم بتغيير حالة القروب
        setSections(prevSections => {
          const updatedSections = [...prevSections];
          
          // البحث عن القسم الذي يحتوي على القروب
          for (let i = 0; i < updatedSections.length; i++) {
            const section = updatedSections[i];
            const groupIndex = section.items.findIndex(item => 
              item.id === groupId && item.type === 'group'
            );
            
            if (groupIndex !== -1) {
              // تحديث حالة القروب
              const updatedGroup = {
                ...section.items[groupIndex],
                status: response.group.status,
                // تحديث أي بيانات أخرى قد تكون تغيرت في الباك إند
                title: response.group.title,
                description: response.group.description,
                link: response.group.link,
                groupType: response.group.type
              };
              
              section.items[groupIndex] = updatedGroup;
              break;
            }
          }
          
          return updatedSections;
        });
        
        const statusText = response.group.status === 'published' ? 'نشر' : 'إخفاء';
        toastService.success(`تم ${statusText} القروب بنجاح`);
      }
    } catch (error) {
      // خطأ في تغيير حالة المجموعة
      toastService.error('حدث خطأ أثناء تغيير حالة القروب');
    }
  };

  // حذف قروب
  const handleDeleteGroup = async (groupId) => {
    try {
      // حذف المجموعة
      
      // إرسال طلب الحذف للباك إند
      const response = await coursesApi.deleteGroup(groupId);
      
      // تحديث واجهة المستخدم بحذف القروب - بنفس طريقة حذف الدرس
      setSections(prevSections => {
        // إنشاء نسخة جديدة من الأقسام باستخدام دالة map
        return prevSections.map(section => ({
          ...section,
          // فلترة العناصر لإزالة القروب المحذوف
          items: section.items.filter(item => !(item.id === groupId && item.type === 'group'))
        }));
      });
      
      // إذا كان هناك قسم محدد مع نفس هذا القروب، قم بتحديثه أيضًا
      if (selectedSection) {
        const hasDeletedGroup = selectedSection.items.some(item => 
          item.id === groupId && item.type === 'group');
        
        if (hasDeletedGroup) {
          setSelectedSection(prev => ({
            ...prev,
            items: prev.items.filter(item => !(item.id === groupId && item.type === 'group'))
          }));
        }
      }
      
      toastService.success('تم حذف القروب بنجاح');
    } catch (error) {
      // خطأ في حذف المجموعة
      toastService.error('حدث خطأ أثناء حذف القروب');
    }
  };
  
  if (loading) {
    return (
      <div className="p-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-500"></div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        {/* رأس الصفحة */}
        <div className="border-b border-gray-200 dark:border-gray-700 p-4 flex justify-between items-center">
          <button 
            onClick={() => navigate('/admin/dashboard/courses/content')}
            className="flex items-center text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400"
          >
            <ArrowLeftIcon className="h-5 w-5 ml-1" />
            <span>العودة إلى قائمة الدورات</span>
          </button>
          
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {course?.name || 'إدارة محتوى الدورة'}
          </h1>
          
          <button
            onClick={() => setShowAddSectionModal(true)}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors duration-200"
          >
            <PlusIcon className="h-5 w-5 ml-1" />
            <span>إضافة قسم</span>
          </button>
        </div>
        
        {/* محتوى الدورة */}
        <div className="p-6">
          {sections.length === 0 ? (
            <div className="text-center py-10 text-gray-500 dark:text-gray-400">
              <BookOpenIcon className="h-16 w-16 mx-auto mb-4 text-gray-400 dark:text-gray-600" />
              <p className="text-lg">لا يوجد محتوى للدورة حالياً</p>
              <p className="mt-2">قم بإضافة قسم جديد للبدء في إنشاء محتوى الدورة</p>
              <button
                onClick={() => setShowAddSectionModal(true)}
                className="mt-4 inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 transition-colors duration-200"
              >
                <PlusIcon className="h-5 w-5 ml-1" />
                <span>إضافة قسم</span>
              </button>
            </div>
          ) : (
            <DndContext 
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sections.map(section => section.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-6">
                  {sections.map(section => (
                    <Section
                      key={section.id}
                      section={section}
                      isExpanded={section.isExpanded}
                      onExpand={() => handleToggleSection(section.id)}
                      onEdit={() => {
                        setSelectedSection(section);
                        setShowEditSectionModal(true);
                      }}
                      onDelete={() => {
                        setSelectedSection(section);
                        setShowDeleteSectionModal(true);
                      }}
                      onAddLesson={() => {
                        setSelectedSection(section);
                        setShowAddLessonModal(true);
                      }}
                      onAddQuiz={() => {
                        setSelectedSection(section);
                        setShowAddQuizModal(true);
                      }}
                      onStatusChange={(sectionId) => handleSectionStatusChange(sectionId)}
                      onLessonStatusChange={(lessonId) => handleLessonStatusChange(lessonId)}
                      onLessonDelete={(lesson) => {
                        handleDeleteLesson(lesson);
                      }}
                      onLessonEdit={(lessonId) => handleEditLesson(lessonId)}
                      onQuizStatusChange={(quizId) => handleQuizStatusChange(quizId)}
                      onQuizDelete={async (quizId) => {
                        try {
                          // فك ارتباط الاختبار من القسم
                          
                          // البحث عن القسم الذي يحتوي على الاختبار
                          let sectionWithQuiz = null;
                          let quizItem = null;
                          
                          for (const section of sections) {
                            const foundQuiz = section.items?.find(item => 
                              item.type === 'quiz' && item.id === quizId
                            );
                            
                            if (foundQuiz) {
                              sectionWithQuiz = section;
                              quizItem = foundQuiz;
                              break;
                            }
                          }
                          
                          if (!sectionWithQuiz || !quizItem) {
                            // لم يتم العثور على الاختبار في أي قسم
                            toastService.error('حدث خطأ أثناء فك ارتباط الاختبار');
                            return;
                          }
                          
                          // فك ارتباط الاختبار من القسم
                          const response = await coursesApi.unlinkExamFromSection(sectionWithQuiz.id, quizId);
                          // تم حذف الاختبار من القسم بنجاح
                          
                          // تحديث قائمة الأقسام لإزالة الاختبار من الواجهة
                          setSections(prevSections => {
                            return prevSections.map(section => {
                              if (section.id !== sectionWithQuiz.id) return section;
                              
                              return {
                                ...section,
                                items: section.items.filter(item => item.id !== quizId || item.type !== 'quiz')
                              };
                            });
                          });
                          
                          toastService.success('تم حذف الاختبار من القسم بنجاح');
                        } catch (error) {
                          // خطأ في فك ارتباط الاختبار من القسم
                          toastService.error('حدث خطأ أثناء فك ارتباط الاختبار من القسم');
                        }
                      }}
                      onQuizEdit={(quizId) => {
                        // تنفيذ تحرير الاختبار بالانتقال إلى صفحة تحرير الاختبار
                        // تحرير الاختبار
                        navigate(`/admin/dashboard/courses/exams/edit/${quizId}`);
                      }}
                      onResourceStatusChange={async (resourceId) => {
                        try {
                          // تغيير حالة المورد
                          
                          // البحث عن الحقيبة الحالية من العناصر
                          let resourceItem = null;
                          let sectionWithResource = null;
                          
                          for (const section of sections) {
                            const foundResource = section.items?.find(item => 
                              item.id === resourceId && item.type === 'resource'
                            );
                            
                            if (foundResource) {
                              resourceItem = foundResource;
                              sectionWithResource = section;
                              break;
                            }
                          }

                          if (!resourceItem) {
                            // لم يتم العثور على المورد
                            toastService.error('لم يتم العثور على الحقيبة');
                            return;
                          }
                          
                          // الحالة الجديدة عكس الحالة الحالية
                          const newStatus = resourceItem.status === 'published' ? 'hidden' : 'published';
                          
                          // تحديث الحالة في واجهة المستخدم فوراً
                          setSections(prevSections => 
                            prevSections.map(section => ({
                              ...section,
                              items: section.items.map(item => 
                                (item.id === resourceId && item.type === 'resource')
                                  ? { ...item, status: newStatus }
                                  : item
                              )
                            }))
                          );
                          
                          // تحديث القسم المحدد أيضًا إذا كان هو نفس القسم الذي تمت تغيير حالة الحقيبة فيه
                          if (selectedSection && sectionWithResource && selectedSection.id === sectionWithResource.id) {
                            setSelectedSection(prev => ({
                              ...prev,
                              items: prev.items.map(item => 
                                (item.id === resourceId && item.type === 'resource')
                                  ? { ...item, status: newStatus }
                                  : item
                              )
                            }));
                          }

                          // تحديث الحالة في قاعدة البيانات
                          const response = await coursesApi.toggleResourceStatus(resourceId);
                          // استجابة تغيير حالة المورد
                          
                          const isPublished = newStatus === 'published';
                          toastService.success(isPublished ? 'تم نشر الحقيبة التعليمية' : 'تم إخفاء الحقيبة التعليمية');
                        } catch (error) {
                          // خطأ في تغيير حالة المورد
                          toastService.error('حدث خطأ أثناء تغيير حالة الحقيبة التعليمية');
                        }
                      }}
                      onResourceDelete={async (resourceId) => {
                        try {
                          // البحث عن الحقيبة الحالية
                          const currentResource = sections
                            .flatMap(section => section.items)
                            .find(item => item.id === resourceId);
                          
                          if (!currentResource) {
                            toastService.error('لم يتم العثور على الحقيبة التعليمية');
                            return;
                          }
                          
                          try {
                            const response = await coursesApi.deleteResource(resourceId);
                            
                            // تحديث واجهة المستخدم بعد نجاح العملية
                            setSections(prevSections =>
                              prevSections.map(section => ({
                                ...section,
                                items: section.items.filter(item => item.id !== resourceId)
                              }))
                            );
                            
                            toastService.success('تم حذف الحقيبة التعليمية');
                          } catch (error) {
                            // خطأ في حذف المورد
                            toastService.error('حدث خطأ أثناء حذف الحقيبة التعليمية');
                          }
                        } catch (error) {
                          // خطأ في حذف المورد
                          toastService.error('حدث خطأ أثناء حذف الحقيبة التعليمية');
                        }
                      }}
                      onResourceEdit={(resource) => {
                        // تحرير المورد
                        
                        // تحضير الحقيبة للتعديل بشكل مباشر (شبيه بتعديل القروب)
                        try {
                          // البحث عن الحقيبة من عناصر القسم (التصحيح هنا من section.resources إلى section.items)
                          const resourceItem = section.items?.find(item => 
                            item.id === resource.id && item.type === 'resource'
                          );
                          
                          // تم العثور على المورد للتحرير
                          
                          if (!resourceItem) {
                            // لم يتم العثور على المورد في عناصر القسم
                            toastService.error('لم يتم العثور على الحقيبة');
                            return;
                          }
                          
                          // إعداد بيانات الحقيبة للتحرير
                          const resourceData = {
                            id: resourceItem.id,
                            title: resourceItem.title || '',
                            description: resourceItem.description || '',
                            type: resourceItem.resourceType || 'file', // نوع الحقيبة (file أو link)
                            url: resourceItem.url || '',
                            file: null, // سيتم تحديده في المودال إذا كان المستخدم يريد تغيير الملف
                            allowDownload: resourceItem.allowDownload !== false // السماح بالتنزيل بشكل افتراضي
                          };
                          
                          // تم تحضير بيانات المورد للتحرير
                          
                          // تعيين الحقيبة والقسم للتحرير
                          setSelectedResource(resourceData);
                          setResourceToEdit(resourceData);
                          setSelectedSection(section);
                          setShowResourceModal(true);
                          
                        } catch (error) {
                          // خطأ في تحضير المورد للتحرير
                          toastService.error('حدث خطأ أثناء تحضير الحقيبة للتحرير');
                        }
                      }}
                      onGroupStatusChange={handleGroupStatusChange}
                      onGroupDelete={handleDeleteGroup}
                      onGroupEdit={handleEditGroup}
                      onAddResource={(resource) => {
                        // تنفيذ إضافة حقيبة
                        // إضافة حقيبة
                        
                        // حساب الترتيب الجديد للحقيبة
                        const currentItems = section.items || [];
                        const maxOrder = currentItems.length > 0 
                          ? Math.max(...currentItems.map(item => parseInt(item.order) || 0))
                          : 0;
                        const newOrder = maxOrder + 1;
                        
                        // بداية إضافة الحقيبة
                        
                        // التحقق من وجود الملف ونوعه
                        if (resource.type === 'file') {
                        }
                        
                        const progressToast = toastService.loading('جاري رفع الحقيبة: 0%', { autoClose: false });
                        coursesApi.addResource(section.id, { 
                          ...resource, 
                          order: newOrder,
                          is_free: resource.is_free || false
                        }, (p) => {
                          toastService.update(progressToast, `جاري رفع الحقيبة: ${p}%`);
                        })
                          .then(response => {
                          toastService.dismiss(progressToast);
                            // تمت إضافة الحقيبة بنجاح
                            
                            // إنشاء كائن الحقيبة الجديدة
                            const newResource = {
                              ...response.data,
                              id: response.data.id,
                              type: 'resource',
                              resourceType: resource.type, // file أو link
                              title: resource.title,
                              description: resource.description,
                              order: newOrder
                            };
                            
                            // تحديث القسم بالحقيبة الجديدة - بنفس أسلوب إضافة الدروس والقروب
                            setSections(prevSections => {
                              return prevSections.map(s => {
                                if (s.id !== section.id) return s;
                                
                                // تأكد من وجود مصفوفة items
                                const currentItems = s.items || [];
                                
                                // إنشاء نسخة جديدة من القسم مع إضافة الحقيبة الجديدة
                                return {
                                  ...s,
                                  items: [...currentItems, newResource]
                                };
                              });
                            });
                            
                            // تحديث القسم المحدد أيضًا إذا كان هو نفس القسم الذي تمت إضافة الحقيبة إليه
                            if (selectedSection && selectedSection.id === section.id) {
                              setSelectedSection(prev => {
                                if (!prev) return prev;
                                const currentItems = prev.items || [];
                                return {
                                  ...prev,
                                  items: [...currentItems, newResource]
                                };
                              });
                            }
                            
                            toastService.success('تمت إضافة الحقيبة بنجاح');
                          })
                          .catch(error => {
                            // خطأ في إضافة الحقيبة
                            toastService.error('حدث خطأ أثناء إضافة الحقيبة');
                            toastService.dismiss(progressToast);
                          });
                      }}
                      onAddGroup={(group) => {
                        // فتح مودال المجموعة للقسم
                        setSelectedSection(section);
                        setShowAddGroupModal(true);
                      }}
                      onItemsReorder={(activeId, overId) => handleLessonsDragEnd({ active: { id: activeId }, over: { id: overId } }, section.id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>
      
      {/* النوافذ المنبثقة */}
      <AddSectionModal
        isOpen={showAddSectionModal}
        onClose={() => setShowAddSectionModal(false)}
        onSubmit={handleAddSection}
      />
      
      <EditSectionModal
        isOpen={showEditSectionModal}
        onClose={() => {
          setShowEditSectionModal(false);
          setSelectedSection(null);
        }}
        onSubmit={handleEditSection}
        section={selectedSection}
      />
      
      <AddLessonModal
        isOpen={showAddLessonModal}
        onClose={() => {
          setShowAddLessonModal(false);
          setSelectedSection(null);
        }}
        onSubmit={handleAddLesson}
        sectionId={selectedSection?.id}
      />
      
      <AddQuizModal
        isOpen={showAddQuizModal}
        onClose={() => {
          setShowAddQuizModal(false);
          setSelectedSection(null);
        }}
        courseId={courseId}
        onAdd={async (data) => {
          if (data.type === 'new') {
            // توجيه المستخدم إلى صفحة إنشاء اختبار جديد
            navigate('/admin/dashboard/courses/exams/create');
          } else if (data.type === 'existing' && data.examId) {
            // ربط الاختبار الموجود بالقسم
            // ربط الاختبار الموجود بالقسم المحدد
            try {
              // الحصول على بيانات الاختبار المضاف
              const examData = await coursesApi.getExamById(data.examId);
              const response = await coursesApi.linkExamToSection(selectedSection.id, data.examId);
              
              // تحديث الواجهة مباشرة بدون إعادة تحميل المحتوى كاملاً
              setSections(prevSections => {
                return prevSections.map(section => {
                  if (section.id !== selectedSection.id) return section;
                  
                  // تجهيز بيانات الاختبار للعرض
                  const quizItem = {
                    id: examData.id,
                    title: examData.title,
                    type: 'quiz',
                    status: examData.status || 'published',
                    order: response.pivot?.order || 0,
                    questionsCount: examData.questions_count || 0,
                    duration: examData.duration || 0
                  };
                  
                  // إضافة الاختبار إلى قائمة عناصر القسم
                  return {
                    ...section,
                    items: [...(section.items || []), quizItem]
                  };
                });
              });
              
              toastService.success('تم إضافة الاختبار بنجاح');
            } catch (error) {
              // معالجة خطأ إضافة الاختبار
              toastService.error('حدث خطأ أثناء إضافة الاختبار');
            }
          }
          setShowAddQuizModal(false);
        }}
      />
      
      <DeleteConfirmModal
        isOpen={showDeleteSectionModal}
        onClose={() => {
          setShowDeleteSectionModal(false);
          setSelectedSection(null);
        }}
        onConfirm={handleDeleteSection}
        itemType="القسم"
        itemTitle={selectedSection?.title}
      />
      

      <EditLessonModal
        isOpen={showEditLessonModal}
        onClose={() => {
          setShowEditLessonModal(false);
          setSelectedLesson(null);
          setSelectedSection(null);
        }}
        onSubmit={handleUpdateLesson}
        lesson={selectedLesson}
        sectionId={selectedSection?.id}
      />
      
      <ResourceModal
        isOpen={showResourceModal}
        onClose={() => {
          setShowResourceModal(false);
          setSelectedResource(null);
          setSelectedSection(null);
          setResourceToEdit(null);
        }}
        onSelect={async (resourceData) => {
          // معالجة اختيار المورد
          try {
            let response;
            if (selectedResource) {
              // تحديث الحقيبة الموجودة
              response = await coursesApi.updateResource(selectedResource.id, {
                ...resourceData,
                sectionId: selectedSection.id
              });
              toastService.success('تم تحديث الحقيبة بنجاح');
            } else {
              // إضافة حقيبة جديدة
              response = await coursesApi.addResource(selectedSection.id, resourceData);
              toastService.success('تمت إضافة الحقيبة بنجاح');
            }

            // تحديث البيانات في الواجهة
            setSections(prevSections => {
              return prevSections.map(section => {
                if (section.id === selectedSection.id) {
                  // نحتفظ بنوع العنصر 'resource' ونوع الحقيبة (file/link) في البيانات
                  const updatedResource = {
                    ...response.data,
                    type: 'resource',
                    resourceType: response.data.type  // الاحتفاظ بنوع الحقيبة من البيانات الأصلية
                  };
                  
                  // تحديث بيانات المورد للواجهة
                  
                  return {
                    ...section,
                    items: selectedResource
                      ? section.items.map(item =>
                          item.id === selectedResource.id
                            ? updatedResource
                            : item
                        )
                      : [...section.items, updatedResource]
                  };
                }
                return section;
              });
            });

            // إغلاق المودال
            setShowResourceModal(false);
            setSelectedResource(null);
            setSelectedSection(null);
          } catch (error) {
            // معالجة خطأ إضافة/تحديث المورد
            toastService.error(
              selectedResource
                ? 'حدث خطأ أثناء تحديث الحقيبة'
                : 'حدث خطأ أثناء إضافة الحقيبة'
            );
          }
        }}
        resource={resourceToEdit}
      />
      
      {/* مودال إضافة قروب */}
      <GroupModal
        isOpen={showAddGroupModal}
        onClose={() => {
          setShowAddGroupModal(false);
          setSelectedSection(null);
        }}
        onSelect={handleAddGroup}
      />
      
      {/* مودال تعديل قروب */}
      <GroupModal
        isOpen={showEditGroupModal}
        onClose={() => {
          setShowEditGroupModal(false);
          setSelectedGroup(null);
          setSelectedSection(null);
        }}
        onSelect={handleUpdateGroup}
        group={selectedGroup}
        isEdit={true}
      />
    </div>
  );
};

export default CourseContentManage;
